import type {SidebarLink, UserShortcut} from '../types'
import {supportedIconColors, supportedIconNames} from '../types'

export function isValidShortcutName(shortcut: UserShortcut): boolean {
  return !!shortcut.name && shortcut.name.trim().length > 0
}

export function isValidShortcutQuery(shortcut: UserShortcut): boolean {
  return !!shortcut.query && shortcut.query.trim().length > 0
}

function isValidShortcutIcon(shortcut: UserShortcut): boolean {
  return supportedIconNames.includes(shortcut.icon)
}

function isValidShortcutColor(shortcut: UserShortcut): boolean {
  return supportedIconColors.includes(shortcut.color)
}

export function isValidShortcut(shortcut: UserShortcut): boolean {
  return (
    isValidShortcutName(shortcut) &&
    isValidShortcutQuery(shortcut) &&
    isValidShortcutIcon(shortcut) &&
    isValidShortcutColor(shortcut)
  )
}

/**
 * Returns the identifier to use for a saved view in a URL segment.
 *
 * Saved views are addressed by their GraphQL global id (`/issues/SSC_kgDOAAi2Mw`), which
 * is what the legacy dashboard emitted and what old bookmarks and shared links still
 * carry. Falls back to the numeric id so links keep working against a backend that
 * doesn't send `globalId` yet, and so numeric URLs bookmarked since the cutover
 * still resolve.
 *
 * API calls (`/users/shortcuts/:id`) and React keys still use the numeric `id`.
 * Issues default-view cookies also use this route identifier so redirects remain
 * canonical; pulls default-view cookies retain their existing numeric-id format.
 */
export function savedViewRouteId(link: Pick<UserShortcut, 'id' | 'globalId'>): string {
  return link.globalId ?? link.id.toString()
}

export function getShortcutsWithSaved(existingShortcuts: SidebarLink[], savedShortcut: SidebarLink) {
  const exists = existingShortcuts.some(shortcut => shortcut.id === savedShortcut.id)

  if (exists) {
    return existingShortcuts.map(shortcut => (shortcut.id === savedShortcut.id ? savedShortcut : shortcut))
  } else {
    return [...existingShortcuts, savedShortcut]
  }
}
